import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The signature element of the landing page: a muster roll — the attendance register
 * every one of our customers already keeps on paper, rendered as the product keeps it.
 *
 * The pattern is deterministic (no Math.random) so the panel looks identical on every
 * render, in every test, and in a screenshot diff. Fridays are the weekly off day, which
 * is what a Bangladeshi factory roster actually looks like.
 */

type Mark = 'in' | 'late' | 'leave' | 'off';

const DAYS = 14;
const FIRST_DAY = 12; // the register opens mid-month, as a real one does

// Row = employee, column = day. Hand-set rather than generated: a real fortnight has
// texture — one person on leave for three days, a cluster of late marks after the off day.
const ROSTER: { name: string; marks: Mark[] }[] = [
    { name: 'Rina Akhter',   marks: ['in', 'in', 'in', 'off', 'in', 'in', 'in', 'in', 'in', 'off', 'in', 'in', 'in', 'in'] },
    { name: 'Kamrul Hasan',  marks: ['in', 'late', 'in', 'off', 'late', 'in', 'in', 'in', 'in', 'off', 'in', 'late', 'in', 'in'] },
    { name: 'Shirin Begum',  marks: ['in', 'in', 'leave', 'off', 'leave', 'leave', 'in', 'in', 'in', 'off', 'in', 'in', 'in', 'in'] },
    { name: 'Tanvir Ahmed',  marks: ['in', 'in', 'in', 'off', 'in', 'in', 'late', 'in', 'in', 'off', 'in', 'in', 'in', 'late'] },
    { name: 'Nasima Khatun', marks: ['in', 'in', 'in', 'off', 'in', 'in', 'in', 'in', 'leave', 'off', 'in', 'in', 'in', 'in'] },
    { name: 'Jashim Uddin',  marks: ['late', 'in', 'in', 'off', 'in', 'in', 'in', 'late', 'in', 'off', 'in', 'in', 'in', 'in'] },
];

// Labels live in the landing namespace under musterRoll.legend.<mark>.
const LEGEND: Mark[] = ['in', 'late', 'leave', 'off'];

const MusterRoll: React.FC = () => {
    const { t } = useTranslation('landing');
    return (
    <figure className="lp-roll">
        <div className="lp-roll-head">
            <span className="lp-roll-eyebrow">{t('musterRoll.title')}</span>
            <span className="lp-roll-meta">{t('musterRoll.meta')}</span>
        </div>

        <div
            className="lp-roll-grid"
            role="img"
            aria-label={t('musterRoll.ariaLabel')}
        >
            <span className="lp-roll-corner" aria-hidden="true" />
            {Array.from({ length: DAYS }, (_, i) => (
                <span className="lp-roll-day" key={`day-${i}`} aria-hidden="true">
                    {FIRST_DAY + i}
                </span>
            ))}

            {ROSTER.map((row, r) => (
                <React.Fragment key={row.name}>
                    <span className="lp-roll-name" aria-hidden="true">{row.name}</span>
                    {row.marks.map((mark, c) => (
                        <span
                            key={`${row.name}-${c}`}
                            aria-hidden="true"
                            className={`lp-cell lp-cell-${mark}`}
                            // Punches land column by column, the way a day actually fills in.
                            style={{ animationDelay: `${(c * ROSTER.length + r) * 14}ms` }}
                        />
                    ))}
                </React.Fragment>
            ))}
        </div>

        <figcaption className="lp-roll-legend">
            {LEGEND.map((mark) => (
                <span className="lp-legend-item" key={mark}>
                    <span className={`lp-cell lp-cell-${mark} lp-cell-static`} aria-hidden="true" />
                    {t(`musterRoll.legend.${mark}`)}
                </span>
            ))}
        </figcaption>
    </figure>
    );
};

export default MusterRoll;
